import { useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { Transition } from '@headlessui/react';

import { useMainMachine } from '../../../utils/useMainMachine';
import { useCrazyAI } from '../../../ai/useCrazyAI';
import { parseVariations } from '../../../ai/variations';
import { TextArea } from '../basic-ui/TextArea';
import { VoiceAndGenerate } from './VoiceAndGenerate';
import { Spinner } from '../basic-ui/Spinner';
import { formatChecksForAI, getVoice } from '../../../utils/utils';
import { inflect } from '../../../utils/string';
import { removeSVGs, restoreSVGs, getHTMLSuggestionContent, isStringHTML } from '../../../utils/html';
import { AI_FEEDBACK_TYPES, SUGGESTION_COUNT, METRICS_PREFIX, AI_PROMPTS } from '../../constants';
import { ACTIONS } from '../../../state/actions';
import { sendMetric } from '../../../utils/metrics';
import { getSegmentAsText } from '../../../utils/segments';

import TickIcon from '@crazyegginc/hatch/dist/images/icon-tick-basic.svg?react';
import BadgeCrossIcon from '@crazyegginc/hatch/dist/images/icon-badge-cross.svg?react';
import BadgeWarningIcon from '@crazyegginc/hatch/dist/images/icon-badge-warning.svg?react';
import BadgeTickIcon from '@crazyegginc/hatch/dist/images/icon-badge-tick.svg?react';
import ExpandIcon from '@crazyegginc/hatch/dist/images/icon-arrow-expand.svg?react';

export function TextSuggestions({ usedVoice, setUsedVoice }) {
  const { useSelector, machine } = useMainMachine();
  const voice = useSelector(({ context }) => context.voice);
  const voices = useSelector(({ context }) => context.brandGuidelines.voices);
  const profile = useSelector(({ context }) => context.brandGuidelines.profile);
  const checks = useSelector(({ context }) => context.brandGuidelines.checks);

  const selectedElement = useSelector(({ context }) => context.selectedElement);
  const pageContext = useSelector(({ context }) => context.pageContext);
  const anonymousId = useSelector(({ context }) => context.anonymousId);
  const userId = useSelector(({ context }) => context.editor.user_id);
  const segment = useSelector(({ context }) => context.segment);
  const [selectedSuggestion, setSelectedSuggestion] = useState();
  const [queryStartTime, setQueryStartTime] = useState(null);
  const [suggestions, setSuggestions] = useState();
  const [retries, setRetries] = useState(0);

  const { html, text, type, closestHeadline, placeholder } = selectedElement;

  const isInput = selectedElement.tagName === 'INPUT';
  const isHTML = isInput ? false : isStringHTML(html);
  let content;
  if (isInput) {
    content = placeholder;
  } else {
    content = isHTML ? html : text;
  }
  const [stableContent, setStableContent] = useState(content);
  const [origElement] = useState(selectedElement);

  const params = useMemo(() => {
    const voiceObject = voices.find((v) => v.name === usedVoice);
    const { name, details } = getSegmentAsText(segment);
    const segmentText = name === 'Everyone' ? null : `${name}\n${details}`;
    const result = {
      url: pageContext.url,
      content: removeSVGs(stableContent),
      title: pageContext.title || '',
      meta_description: pageContext.meta_description || '',
      voice: voiceObject.definition,
      voiceSample: voiceObject.sample,
      suggestion_count: SUGGESTION_COUNT,
      element_type: type,
      segment: segmentText,
      siteContext: profile.context,
      audience: profile.audience,
      checks: JSON.stringify((checks?.filter((check) => check.enabled === true) ?? []).map(formatChecksForAI)),
    };
    if (type !== 'headline') {
      result.nearest_headline = closestHeadline || '';
    }
    return result;
  }, [pageContext, stableContent, usedVoice, closestHeadline, type, segment, voices, profile, checks]);

  const context = useMemo(
    () => ({
      url: `${location.pathname}${location.search}`,
    }),
    [],
  );

  const { data, sendFeedback, refetch, error } = useCrazyAI({
    type: AI_PROMPTS.TEXT_GENERATOR,
    params,
    context,
  });

  useEffect(() => {
    if (!data?.ready && !queryStartTime) {
      setQueryStartTime(performance.now());
    } else if (data?.ready && !error && queryStartTime) {
      sendMetric(`${METRICS_PREFIX}_element_text_generated`, {
        anonymous_id: anonymousId,
        user_id: userId,
        elapsed_time: Math.round(performance.now() - queryStartTime),
      });
      setQueryStartTime(null);
    } else if (error) {
      setQueryStartTime(null);
    }
  }, [data, error, queryStartTime, userId, anonymousId]);

  useEffect(() => {
    if (error) {
      machine.send({ type: ACTIONS.TEXT_GENERATION_ERROR, error, params });
    }
  }, [error, machine, params]);

  useEffect(() => {
    let list;
    let issue = false;
    const isRenderable = (suggestion) => !isHTML || Boolean(getHTMLSuggestionContent(suggestion).trim());
    try {
      if (data?.ready) {
        list = parseVariations(data.text, { isRenderable });
        if (list.length !== SUGGESTION_COUNT) {
          issue = true;
        }
      } else if (data?.text) {
        list = parseVariations(data.text, { partial: true, isRenderable });
      }
    } catch {
      issue = Boolean(data?.ready);
    }
    if (issue && retries < 3) {
      refetch();
      setRetries((x) => x + 1);
    } else if (data?.ready || list) {
      setSuggestions(list);
    }
  }, [data, isHTML, refetch, retries]);

  function applySuggestion(suggestion) {
    setSelectedSuggestion(suggestion);
    if (isInput) {
      machine.send({
        type: ACTIONS.SET_SELECTED_EL_ATTRIBUTE,
        attribute: 'placeholder',
        value: suggestion,
        voice: usedVoice,
      });
    } else if (isHTML) {
      const newHTML = restoreSVGs(stableContent, suggestion);
      machine.send({ type: ACTIONS.SET_SELECTED_EL_HTML, html: newHTML, voice: usedVoice });
    } else {
      machine.send({ type: ACTIONS.SET_SELECTED_EL_TEXT, text: suggestion, voice: usedVoice });
    }

    sendFeedback(AI_FEEDBACK_TYPES.IMPLICIT_POSITIVE);
  }

  function revertSuggestion() {
    setSelectedSuggestion(null);
    if (isInput) {
      machine.send({
        type: ACTIONS.SET_SELECTED_EL_ATTRIBUTE,
        attribute: 'placeholder',
        value: stableContent,
      });
    } else if (isHTML) {
      machine.send({ type: ACTIONS.SET_SELECTED_EL_HTML, html: stableContent });
    } else {
      machine.send({ type: ACTIONS.SET_SELECTED_EL_TEXT, text: stableContent });
    }
  }

  return (
    <>
      <TextArea
        value={isInput ? origElement.placeholder : origElement.text}
        rows={2}
        readOnly
        label="Content"
        labelClassName="!font-normal !text-gray-200"
        className="focus:!border-transparent !cursor-default"
      />
      <div data-testid="ai-suggestions">
        <>
          {suggestions?.map((suggestion) => (
            <TextSuggestion
              key={suggestion}
              onClick={() => (selectedSuggestion === suggestion ? revertSuggestion() : applySuggestion(suggestion))}
              selected={selectedSuggestion === suggestion}
              anySelected={!!selectedSuggestion}
              usedVoice={usedVoice}
              disabled={!data?.ready}
            >
              {isHTML ? getHTMLSuggestionContent(suggestion) : suggestion}
            </TextSuggestion>
          ))}
          {!error &&
            !data?.ready &&
            Array.from({ length: SUGGESTION_COUNT - (suggestions?.length ?? 0) }).map((x, i) => (
              <div
                key={SUGGESTION_COUNT - i}
                className={classNames(
                  'w-[240px] h-[70px] mt-2.5 rounded-md bg-gray-500/40 animate-skeleton',
                  'bg-gradient-to-r from-gray-500/40 via-[20%] via-gray-500/10 to-[40%] to-gray-500/40 bg-[length:600px]',
                )}
              />
            ))}
        </>
      </div>
      <Transition
        show={Boolean(data?.ready)}
        enter="transition-opacity duration-700 delay-500"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-0"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <VoiceAndGenerate
          onClick={() => {
            setUsedVoice(getVoice(voice, voices));
            setSelectedSuggestion(null);
            setRetries(0);
            if (content === stableContent) {
              refetch();
            } else {
              setStableContent(content);
            }
          }}
        />
      </Transition>
      {selectedSuggestion ? <BrandCheckResult /> : null}
    </>
  );
}

export function TextSuggestion({ children, onClick, selected, anySelected, usedVoice, disabled, color }) {
  const { useSelector } = useMainMachine();

  let voiceColor = useSelector(
    ({ context }) => context.brandGuidelines.voices.find((v) => v.name === usedVoice)?.color,
  );

  if (color) {
    voiceColor = color;
  }

  return (
    <button
      type="button"
      disabled={disabled}
      style={{ '--selectedvoice': voiceColor }}
      className={classNames(
        'relative text-sm text-left mt-2.5 p-2.5 rounded-md border',
        'bg-selectedvoice/10 text-selectedvoice',
        {
          'border-selectedvoice': selected,
          'border-selectedvoice/50 hover:border-selectedvoice opacity-50 hover:opacity-100': !selected && anySelected,
          'border-selectedvoice/50 hover:border-selectedvoice': !selected && !anySelected,
        },
      )}
      onClick={onClick}
    >
      {selected && (
        <div
          className={classNames(
            'absolute top-0 right-0 w-[17px] h-[17px] rounded-bl rounded-tr flex items-center justify-center text-gray-700',
            'bg-selectedvoice',
          )}
        >
          <TickIcon className="h-2 w-2 fill-current" aria-label="selected suggestion" />
        </div>
      )}
      {children}
    </button>
  );
}

function BrandCheckResult() {
  const { useSelector, machine } = useMainMachine();

  const configured = useSelector(({ context }) => Boolean(context.brandGuidelines?.updatedAt));
  const selectedElement = useSelector(({ context }) => context.selectedElement);

  const failedChecks = useSelector(({ context }) =>
    context.brandCheckResults
      ?.filter((e) => e.selector === selectedElement.selector)
      .filter(
        (e) =>
          !Object.entries(context.editor.pages.find((p) => p.id === context.activePageId).ignoredChecks).some(
            ([c, s]) => e.check_id === c && s.includes(e.selector),
          ),
      ),
  );
  const isLoading = useSelector(({ context }) => Object.values(context.runningCheckRefs).filter(Boolean).length > 0);

  if (!configured) return null;

  if (!selectedElement.isCheckable) {
    return (
      <div className="flex items-start font-semibold text-white mt-7">
        <BadgeWarningIcon className="w-4 h-4 fill-current mr-2.5 mt-1 shrink-0" />
        This element cannot be checked for brand compliance.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-between mt-7">
        <div className="flex items-center space-x-2.5">
          <Spinner className="w-3.5 h-3.5" />
          <span>Checking guidelines...</span>
        </div>
      </div>
    );
  }

  if (failedChecks?.length > 0) {
    return (
      <button
        type="button"
        className="flex items-center justify-between mt-7"
        onClick={() => {
          machine.send({ type: ACTIONS.BACK_TO_CHECK_RESULTS });
        }}
      >
        <div className="font-semibold flex items-center">
          <div className="relative mr-2.5">
            <BadgeCrossIcon className="text-[#FF993B] w-4 h-4 fill-current z-10 relative" />
            <div className="absolute rounded-full bg-white w-3 h-2.5 top-0.5 left-0.5 z-0" />
          </div>
          {failedChecks.length} {inflect('issue', failedChecks.length)} in this element
        </div>
        <div>
          <ExpandIcon className="w-3 h-3 fill-current rotate-90" />
        </div>
      </button>
    );
  }

  return (
    <div className="flex items-center font-semibold text-white mt-7">
      <div className="relative mr-2.5">
        <BadgeTickIcon className="w-4 h-4 fill-current text-green-500 z-10 relative" />
        <div className="absolute rounded-full bg-white w-3 h-2.5 top-0.5 left-0.5 z-0" />
      </div>
      Brand compliant
    </div>
  );
}
